/**
 * Pure gating predicate for the "Extract details" attachment action —
 * ADR-0083.
 *
 * Sibling to `canExtractBookingFromAttachment` — same gating model
 * (canEdit + quota slot remaining + uploaded + eligible MIME), plus a
 * label check against the `DocumentSchema` registry. Labels without a
 * registered schema (`confirmation`, `ticket`, `boarding-pass` —
 * those belong to ADR-0081's booking parser; `receipt`, `invoice`,
 * `other` — out of scope for v1) return false here so the action
 * doesn't surface.
 *
 * Owner-only is enforced server-side (the API refuses the call for
 * non-owners); the mobile predicate doesn't try to model trip
 * sharing here because `canEdit` already gates write-class actions
 * on share role.
 */

import type { Attachment } from '@my-fullstack-app/itinerary-types';
import { getDocumentSchema } from '@my-fullstack-app/itinerary-types';
import type { AiQuotaBucket } from '@my-fullstack-app/trpc-types';
import { isExtractorEligibleMime } from '@my-fullstack-app/trpc-types';

export function canExtractVaultDocument(
  attachment: Attachment,
  opts: {
    canEdit: boolean;
    extractQuotaStatus: AiQuotaBucket | null | undefined;
  },
): boolean {
  if (!opts.canEdit) return false;
  const quota = opts.extractQuotaStatus;
  if (!quota || quota.cap <= 0) return false;
  if (quota.used >= quota.cap) return false;
  if (attachment.state !== 'uploaded') return false;
  if (!isExtractorEligibleMime(attachment.mimeType)) return false;
  if (!getDocumentSchema(attachment.label)) return false;
  return true;
}
